import { FC, useEffect, useMemo, useState } from 'react';

import { Loading } from '@shared/components/Loading';

import { formatDate, getBrandItemName } from '../../giftSeller/utils/delivery.utils';
import {
  formatWon,
  getSettlementBrandId,
  ceilWon,
  SettlementLine,
} from '../../giftSeller/utils/settlement.utils';
import { getKstDateParts, NO_BRAND, SortDirection, toYmd } from '../utils/settlement.utils';

// ─────────────────────────────────────────────
// SortHeader
// ─────────────────────────────────────────────

/** `SortHeader` 컴포넌트의 props */
export interface SortHeaderProps {
  /** 헤더에 표시할 레이블 */
  label: string;
  /** 현재 이 컬럼이 정렬 기준인지 여부 */
  active: boolean;
  /** 현재 정렬 방향 */
  direction: SortDirection;
  /** 텍스트 정렬 방향 (기본값: 'left') */
  align?: 'left' | 'right';
  /** 클릭 시 호출되는 정렬 토글 핸들러 */
  onClick: () => void;
}

/**
 * 클릭 시 정렬 방향을 전환하는 테이블 헤더 셀 컴포넌트.
 * 활성화된 컬럼은 진한 색상의 정렬 화살표(▲/▼)를 표시한다.
 */
export const SortHeader: FC<SortHeaderProps> = ({
  label,
  active,
  direction,
  align = 'left',
  onClick,
}) => (
  <th
    onClick={onClick}
    className={`cursor-pointer select-none px-3 py-2 text-${align} hover:text-gray-700`}
  >
    {label}
    <span className={`ml-1 inline-block ${active ? 'text-gray-700' : 'text-gray-300'}`}>
      {active && direction === 'asc' ? '▲' : '▼'}
    </span>
  </th>
);

// ─────────────────────────────────────────────
// DeliveredEntriesSection
// ─────────────────────────────────────────────

/** `DeliveredEntriesSection` 컴포넌트의 props */
export interface DeliveredEntriesSectionProps {
  /** 섹션 상단에 표시할 설명 문구 (선택) */
  description?: string;
  /** 테이블에 렌더링할 정산 라인 목록 */
  lines: SettlementLine[];
  /** 데이터 로딩 중 여부 */
  loading: boolean;
  /** 브랜드별 뷰 여부 (admin 또는 여러 브랜드 담당 셀러). true이면 '업체' 컬럼을 추가 표시 */
  brandView: boolean;
  /** 데이터가 없을 때 표시할 메시지 */
  emptyMessage: string;
  /** 요약 카드의 금액 레이블 */
  amountLabel: string;
  /** 업체 필터 UI 표시 여부 (기본값: true) */
  showFilters?: boolean;
  /** 배송완료일 기간 필터 UI 표시 여부 (기본값: false) */
  showDateFilter?: boolean;
  /** 상품명을 truncate 없이 전체 표시할지 여부 (기본값: false → 200px truncate) */
  fullItemName?: boolean;
  /** 정산금액 컬럼 헤더 레이블 (기본값: '= 정산금액(VAT 포함)') */
  settlementAmountLabel?: string;
  /**
   * 다음 정산 예정일 정보.
   * 값이 있으면 합계 행 왼쪽에 정산 예정일을 표시한다.
   */
  nextSettlementInfo?: { year: number; month: number; day: number } | null;
}

/**
 * 배송완료 내역 또는 정산예정 내역을 테이블로 렌더링하는 공용 섹션 컴포넌트.
 *
 * - 관리자 뷰에서는 업체 필터를 제공한다.
 * - 필터링된 결과의 건수·수량·정산금액 요약 카드를 상단에 표시한다.
 * - `showFilters={false}`로 업체 필터 UI를 숨길 수 있다 (정산예정 섹션에서 사용).
 */
export const DeliveredEntriesSection: FC<DeliveredEntriesSectionProps> = ({
  description,
  lines,
  loading,
  brandView,
  emptyMessage,
  amountLabel,
  showFilters = true,
  showDateFilter = false,
  fullItemName = false,
  settlementAmountLabel = '= 정산금액(VAT 포함)',
  nextSettlementInfo,
}) => {
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  /** 배송완료일 기간 빠른 선택 (이번달/지난달) */
  const setMonthRange = (offset: number) => {
    const { year, month } = getKstDateParts();
    const base = new Date(year, month - 1 + offset, 1);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    setDateFrom(toYmd(first));
    setDateTo(toYmd(last));
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  const brandOptions = useMemo(() => {
    const map = new Map<string, string>();
    let hasNoBrand = false;
    lines.forEach((l) => {
      const d = l.delivery;
      const id = getSettlementBrandId(d);
      if (id) {
        const name =
          typeof d.giftItemBrand === 'object' && d.giftItemBrand
            ? d.giftItemBrand.name
            : '';
        map.set(id, name || id);
      } else {
        hasNoBrand = true;
      }
    });
    const options = Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    if (hasNoBrand) options.push({ id: NO_BRAND, name: '빈업체' });
    return options;
  }, [lines]);

  useEffect(() => {
    if (selectedBrand !== 'all' && !brandOptions.some((b) => b.id === selectedBrand)) {
      setSelectedBrand('all');
    }
  }, [brandOptions, selectedBrand]);

  const filteredLines = useMemo(() => {
    // 배송완료일 기간 경계 (dateTo는 해당일 끝까지 포함)
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return lines.filter((l) => {
      // 업체 필터
      if (selectedBrand !== 'all') {
        const brandId = getSettlementBrandId(l.delivery) || NO_BRAND;
        if (brandId !== selectedBrand) return false;
      }
      // 배송완료일 기간 필터
      if (from || to) {
        const deliveredAt = l.delivery.shipping?.deliveredAt;
        if (!deliveredAt) return false;
        const d = new Date(deliveredAt);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [lines, selectedBrand, dateFrom, dateTo]);

  const totalQuantity = useMemo(
    () => filteredLines.reduce((sum, l) => sum + l.quantity, 0),
    [filteredLines],
  );
  const totalAmount = useMemo(
    () => filteredLines.reduce((sum, l) => sum + l.total, 0),
    [filteredLines],
  );
  const totalCourier = useMemo(
    () => filteredLines.reduce((sum, l) => sum + l.courierFee, 0),
    [filteredLines],
  );
  const missingSettlementCount = useMemo(
    () => filteredLines.filter((l) => !l.hasSettlementInfo).length,
    [filteredLines],
  );

  return (
    <div className="mb-14">
      {description && <p className="mb-4 text-sm text-gray-600">{description}</p>}

      {missingSettlementCount > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          정산정보(추가단가·택배비)가 설정되지 않은 공급사 건이 {missingSettlementCount}건
          있습니다. 해당 건은 도매가만 합산되며, 관리자에게 정산정보 설정을 요청하세요.
        </div>
      )}

      {/* 배송완료일 기간 필터 */}
      {showDateFilter && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-500">배송완료일</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1"
          />
          <span className="text-gray-400">~</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1"
          />
          <button
            onClick={() => setMonthRange(0)}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
          >
            이번달
          </button>
          <button
            onClick={() => setMonthRange(-1)}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
          >
            지난달
          </button>
          <button
            onClick={clearDateRange}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
          >
            전체기간
          </button>
        </div>
      )}

      {/* 업체 필터 */}
      {showFilters && brandOptions.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedBrand('all')}
            className={`rounded-full border px-3 py-1 text-sm ${
              selectedBrand === 'all'
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          {brandOptions.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelectedBrand(b.id)}
              className={`rounded-full border px-3 py-1 text-sm ${
                selectedBrand === b.id
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {/* 요약 카드 */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-green-700">{filteredLines.length}</p>
          <p className="text-sm text-gray-500">건수</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-indigo-700">{totalQuantity}</p>
          <p className="text-sm text-gray-500">총 수량</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-rose-700">
            {ceilWon(totalAmount).toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">{amountLabel}</p>
        </div>
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : !lines.length ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {emptyMessage}
        </div>
      ) : filteredLines.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          선택한 조건에 해당하는 건이 없습니다.
          <button
            onClick={() => {
              setSelectedBrand('all');
              clearDateRange();
            }}
            className="ml-2 text-indigo-600 hover:underline"
          >
            전체 보기
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                {brandView && <th className="px-3 py-2 text-left">업체</th>}
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">배송요청일</th>
                <th className="px-3 py-2 text-left">배송완료일</th>
                <th className="px-3 py-2 text-left">택배사</th>
                <th className="px-3 py-2 text-left">송장번호</th>
                <th className="px-3 py-2 text-right">도매단가(+소량)</th>
                <th className="px-3 py-2 text-right">× 수량</th>
                <th className="px-3 py-2 text-right">+ 택배비</th>
                <th className="px-3 py-2 text-right">{settlementAmountLabel}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredLines.map((l) => {
                const d = l.delivery;
                return (
                  <tr key={d._id} className="hover:bg-gray-50">
                    {brandView && (
                      <td className="whitespace-nowrap px-3 py-3 text-xs font-medium text-gray-700">
                        {typeof d.giftItemBrand === 'object' && d.giftItemBrand?.name ? (
                          d.giftItemBrand.name
                        ) : (
                          <span className="text-gray-300">빈업체</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <p
                        className={`font-medium ${
                          fullItemName ? '' : 'max-w-[200px] truncate'
                        }`}
                        title={getBrandItemName(d)}
                      >
                        {getBrandItemName(d)}
                      </p>
                      <p className="text-[11px] text-gray-400">{d.recipient?.name || '-'}</p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      {formatDate(d.recipient?.submittedAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      {formatDate(d.shipping?.deliveredAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600">
                      {d.shipping?.carrier || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-600">
                      {d.shipping?.trackingNumber || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {l.wholePrice.toLocaleString()}
                      {l.extraUnitPrice > 0 && (
                        <span className="text-amber-600">
                          (+{l.extraUnitPrice.toLocaleString()})
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {l.quantity}개
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {l.courierFee > 0 ? (
                        l.courierFee.toLocaleString()
                      ) : l.courierFreeByQuantity ? (
                        <span className="text-[11px] text-gray-400">
                          무료({l.courierFreeMinQuantity}충족)
                        </span>
                      ) : l.courierBundled ? (
                        <span className="text-[11px] text-gray-400">묶음면제</span>
                      ) : l.courierFeeBase > 0 ? (
                        '0'
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <span className="font-semibold tabular-nums text-gray-800">
                        {formatWon(l.total)}
                      </span>
                      {!l.hasSettlementInfo && (
                        <span className="block text-[10px] text-amber-600">
                          정산정보 미설정
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-sm font-semibold">
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={brandView ? 7 : 6}>
                  <span>합계 ({filteredLines.length}건)</span>
                  {nextSettlementInfo && (
                    <span className="ml-4 text-sm font-medium text-indigo-600">
                      다음 정산 예정일:{' '}
                      <span className="font-bold text-indigo-700">
                        {nextSettlementInfo.year}년 {nextSettlementInfo.month}월{' '}
                        {nextSettlementInfo.day}일
                      </span>
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {totalQuantity}개
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {totalCourier > 0 ? totalCourier.toLocaleString() : '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
                  {formatWon(ceilWon(totalAmount))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
